// packages/web-ui/src/state/views.ts
//
// LOW-8 lineage note — ownership history is non-linear: this file was first
// scaffolded as a stub under W2-S1, extended for drag/edit/persist under
// W4-S2, gained the response-view list under W4-S3, then re-added in
// commit cf45f55 after an interim revert. Runtime semantics are
// straightforward; the lineage just doesn't fit a single-story header.
//
// `views` zustand slice. Owns:
//   - `viewsList`        — saved views (server is source-of-truth)
//   - `pinnedByViewId`   — local-only pin map persisted to `localStorage`
//   - `activeViewId`     — currently-mounted view in the renderer
//
// The server's NodeAutoViewWriter writes every view directly to its
// permanent path before emitting `kb:viewRendered`, so the slice no longer
// tracks a separate "pending" list or a keep/discard gate — every view is
// already saved the moment it appears in chat.
//
// All API failures are returned as `Result.err` envelopes (CLAUDE.md
// "Coding Standards"). The slice translates these into local rollback +
// `lastError` for the UI to surface via toast.

import { createStore } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';
import type { Result } from '@tessa-lean/base';
import type {
  LayoutIntent,
  ReactFlowLayoutJson,
  ViewLayoutPutResponse,
  ViewScopePutResponse,
} from '@tessa-lean/base/api-schemas/index.js';
import {
  apiCall,
  apiCallBinary,
  type ApiContext,
  type ApiError,
  type BinaryResult,
} from '../api/client.js';
import { viewsApi as defaultViewsApi } from '../api/clients/views.js';

// ---- Wire shapes (co-located with the slice) -----------------------------

/**
 * IV3-S5 — manifest subset the SPA reads to render raster rows. Mirrors the
 * `ViewManifest` (api-schemas/views.ts) fields the list view needs. The GET
 * /views handler already returns `manifest` on each summary; additive +
 * optional so legacy/manifest-less views deserialize unchanged.
 */
export interface ViewManifestSummary {
  engine: 'deterministic' | 'llm-image' | 'diagram-as-code';
  sysmlSourcePath: string;
  sceneContentHash?: string;
  illustrative?: boolean;
  connectivityVerdict?: 'unverifiable';
  /** Optional workspace-relative PNG path for raster views. */
  imagePath?: string;
  /**
   * LV2-S2 — the sticky per-view LEAD representation. `'sketch'` leads with the
   * deterministic React Flow diagram; `'llm-image'` leads with the raster. The
   * other representation + the SysML source are ALWAYS one action away
   * regardless of this value — the lead only chooses the default surface, it
   * never removes the alternative. Absent ⇒ `'sketch'` (see
   * `effectiveLeadEngine`); legacy / deterministic manifests omit it.
   */
  leadEngine?: 'sketch' | 'llm-image';
}

export interface ViewSummaryWire {
  viewId: string;
  viewName: string;
  lastModified: string;
  thumbnailUrl: string;
  sourcePath: string;
  manifest?: ViewManifestSummary;
}

export interface ViewRenameResponseWire {
  viewId: string;
  viewName: string;
}

export interface ViewDeleteResponseWire {
  deleted: true;
}

export interface ViewsDeleteAllResponseWire {
  deleted: number;
}

// ---- Domain shapes -------------------------------------------------------

export interface ViewSummary {
  viewId: string;
  viewName: string;
  lastModified: string;
  thumbnailUrl: string;
  sourcePath: string;
  /** IV3-S5 — present when the view dir carries a schema-valid manifest.json. */
  manifest?: ViewManifestSummary;
}

// ---- Wire helpers (apiCall wrappers) ------------------------------------

/**
 * Tiny adapter set — keeps the URL templates in one place so tests can mock
 * the network at the `fetchImpl` level (not the wrapper level). Each call
 * returns `Result<T, ApiError>` per CLAUDE.md "Coding Standards".
 */
export interface ViewsWireAdapter {
  listViews: (ctx: ApiContext) => Promise<Result<ViewSummaryWire[], ApiError>>;
  renameView: (
    ctx: ApiContext,
    viewId: string,
    newName: string,
  ) => Promise<Result<ViewRenameResponseWire, ApiError>>;
  deleteView: (
    ctx: ApiContext,
    viewId: string,
  ) => Promise<Result<ViewDeleteResponseWire, ApiError>>;
  deleteAllViews: (
    ctx: ApiContext,
  ) => Promise<Result<ViewsDeleteAllResponseWire, ApiError>>;
  exportPng: (
    ctx: ApiContext,
    viewId: string,
  ) => Promise<Result<BinaryResult, ApiError>>;

  // W4-S2 — layout I/O + SVG export. Defaults delegate to the typed
  // `viewsApi` client (W4-S1 / W4-S2). Tests inject mocks here.
  getLayout?: (
    ctx: ApiContext,
    viewId: string,
  ) => Promise<Result<ReactFlowLayoutJson, ApiError>>;
  saveLayout?: (
    ctx: ApiContext,
    viewId: string,
    layout: ReactFlowLayoutJson,
  ) => Promise<Result<ViewLayoutPutResponse, ApiError>>;
  exportSvg?: (
    ctx: ApiContext,
    viewId: string,
    layoutId?: string,
  ) => Promise<Result<BinaryResult, ApiError>>;

  /**
   * LV2-S2 — persist the sticky per-view lead engine via
   * `PUT /views/:viewId/lead-engine`. Optional so legacy adapters / tests that
   * don't exercise the switcher needn't wire it; the slice action surfaces a
   * `CAPABILITY_MISSING` error when it's absent.
   */
  setLeadEngine?: (
    ctx: ApiContext,
    viewId: string,
    leadEngine: 'sketch' | 'llm-image',
  ) => Promise<Result<{ leadEngine: 'sketch' | 'llm-image' }, ApiError>>;

  /**
   * Phase 2 — clear a view's scope ("Show all" reveal) via
   * `PUT /views/:viewId/scope` with an empty scope (server strips it). Optional
   * so legacy adapters / tests needn't wire it; the slice action surfaces a
   * `CAPABILITY_MISSING` error when it's absent.
   */
  clearScope?: (
    ctx: ApiContext,
    viewId: string,
  ) => Promise<Result<ViewScopePutResponse, ApiError>>;
}

export const defaultViewsWireAdapter: ViewsWireAdapter = {
  listViews: (ctx) =>
    apiCall<undefined, ViewSummaryWire[]>(ctx, 'GET', '/api/v1/views'),
  renameView: (ctx, viewId, newName) =>
    apiCall<{ newName: string }, ViewRenameResponseWire>(
      ctx,
      'POST',
      `/api/v1/views/${encodeURIComponent(viewId)}/rename`,
      { newName },
    ),
  deleteView: (ctx, viewId) =>
    apiCall<undefined, ViewDeleteResponseWire>(
      ctx,
      'DELETE',
      `/api/v1/views/${encodeURIComponent(viewId)}`,
    ),
  deleteAllViews: (ctx) =>
    apiCall<undefined, ViewsDeleteAllResponseWire>(ctx, 'DELETE', '/api/v1/views'),
  exportPng: (ctx, viewId) =>
    apiCallBinary(
      ctx,
      'GET',
      `/api/v1/views/${encodeURIComponent(viewId)}/export.png`,
      { accept: 'image/png', filenameHint: `${viewId}.png` },
    ),
  // W4-S2 layout / svg-export delegates to the typed viewsApi client.
  getLayout: (ctx, viewId) => defaultViewsApi.getLayout(ctx, viewId),
  saveLayout: (ctx, viewId, layout) => defaultViewsApi.saveLayout(ctx, viewId, layout),
  exportSvg: (ctx, viewId, layoutId) => defaultViewsApi.exportSvg(ctx, viewId, layoutId),
  // LV2-S2 — sticky per-view lead-engine switch.
  setLeadEngine: (ctx, viewId, leadEngine) =>
    apiCall<{ leadEngine: 'sketch' | 'llm-image' }, { leadEngine: 'sketch' | 'llm-image' }>(
      ctx,
      'PUT',
      `/api/v1/views/${encodeURIComponent(viewId)}/lead-engine`,
      { leadEngine },
    ),
  // Phase 2 — "Show all" scope clear.
  clearScope: (ctx, viewId) => defaultViewsApi.clearScope(ctx, viewId),
};

// ---- Slice contract ------------------------------------------------------

export interface ViewsSliceState {
  /** Saved-view list (Arch §8.3). Empty until `loadViewsList` resolves. */
  viewsList: ViewSummary[];
  /** Pin map; absent key === not pinned. Value is the pinnedAt epoch. */
  pinnedByViewId: Record<string, number>;
  /** Currently-active view id (the one rendered in the Views tab). */
  activeViewId: string | null;
  /** localStorage key for pin persistence (per-workspace would use a hash). */
  pinnedPersistKey: string;
  /** Loading state for the views-list fetch. */
  loading: boolean;
  /** Last error message — surfaced as a banner / toast by the consumer. */
  lastError: ApiError | null;

  // ── W4-S2 layout / dirty / save plumbing ───────────────────────────────
  /** Per-view layout cache. Populated on `loadView` + each successful save. */
  layoutsById: Record<string, ReactFlowLayoutJson>;
  /** Per-view dirty flag (true when there are unsaved local edits). */
  dirtyByViewId: Record<string, boolean>;
  /** Per-view last-saved epoch (ms). Absent key === never saved. */
  lastSavedAtByViewId: Record<string, number>;
  /** Per-view layoutId from the most recent save (used by export.svg cache). */
  layoutIdByViewId: Record<string, string>;
}

export const VIEWS_INITIAL_STATE: ViewsSliceState = {
  viewsList: [],
  pinnedByViewId: {},
  activeViewId: null,
  pinnedPersistKey: 'tessa-lean.views.pinned',
  loading: false,
  lastError: null,
  layoutsById: {},
  dirtyByViewId: {},
  lastSavedAtByViewId: {},
  layoutIdByViewId: {},
};

export interface ViewsSliceActions {
  loadViewsList: () => Promise<Result<void, ApiError>>;
  pinView: (viewId: string, pinned: boolean) => void;
  renameView: (
    viewId: string,
    newName: string,
  ) => Promise<Result<{ viewId: string; viewName: string }, ApiError>>;
  deleteView: (viewId: string) => Promise<Result<void, ApiError>>;
  /**
   * Bulk-wipe every saved view. Optimistically clears the in-memory list +
   * the pin map; rolls back on server failure. The number of view dirs the
   * server removed is returned in the success envelope.
   */
  deleteAllViews: () => Promise<Result<{ deleted: number }, ApiError>>;
  exportPng: (viewId: string) => Promise<Result<BinaryResult, ApiError>>;

  /** Local-only setter when the user opens a view tab. */
  setActiveViewId: (viewId: string | null) => void;
  /** SSE handler — server emits `views:listChanged` after a keep / rename / delete. */
  refreshAfterListChanged: () => Promise<void>;

  // ── W4-S2 actions (drag/edit/persist + svg export) ─────────────────────
  /** Fetch the persisted layout for a view; populates `layoutsById`. */
  loadView: (viewId: string) => Promise<Result<ReactFlowLayoutJson, ApiError>>;
  /**
   * Persist a layout. Optimistically clears the dirty flag + caches the
   * layout; rolls back on failure (leaving dirty=true so the next edit /
   * explicit save retries).
   */
  saveLayout: (
    viewId: string,
    layout: ReactFlowLayoutJson,
  ) => Promise<Result<ViewLayoutPutResponse, ApiError>>;
  /** Mark a view dirty (renderer drag/edit). */
  markViewDirty: (viewId: string) => void;
  /** Trigger an SVG export (binary download) for the named view. */
  exportSvg: (
    viewId: string,
    layoutId?: string,
  ) => Promise<Result<BinaryResult, ApiError>>;

  /**
   * LV2-S2 — flip the sticky per-view lead engine. Optimistically patches the
   * matching view's `manifest.leadEngine` (creating a minimal manifest if the
   * view had none), then persists via the lead-engine PUT route. Rolls back the
   * in-memory patch + records `lastError` on failure. ONLY the target view is
   * touched — every other view's lead is unaffected (sticky PER view). The
   * sketch + SysML source stay reachable regardless of the chosen lead.
   */
  setLeadEngine: (
    viewId: string,
    leadEngine: 'sketch' | 'llm-image',
  ) => Promise<Result<{ leadEngine: 'sketch' | 'llm-image' }, ApiError>>;

  /**
   * Phase 2 — clear a view's scope ("Show all"). Optimistically strips
   * `viewScope` from the cached layout, PUTs the empty scope, rolls back on
   * failure. The actual visual refresh is a re-fetch by the card/renderer.
   */
  clearScope: (
    viewId: string,
  ) => Promise<Result<ViewScopePutResponse, ApiError>>;

  /**
   * Test/debug helper — reset slice state. Action functions are preserved.
   * Per-slice prefixed (MED-2) so the RootStore composition no longer
   * collides on a shared `_reset` name.
   */
  _resetViews: () => void;
}

export type ViewsSlice = ViewsSliceState & ViewsSliceActions;

// ---- Slice config + helpers ---------------------------------------------

export interface ViewsSliceConfig {
  api?: ViewsWireAdapter;
  apiContext?: ApiContext;
  /** Inject a `localStorage`-like for pin persistence (tests). */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** Inject `Date.now` for deterministic pinnedAt timestamps in tests. */
  now?: () => number;
}

function defaultApiContext(): ApiContext {
  const token =
    typeof localStorage !== 'undefined'
      ? localStorage.getItem('tessaLeanToken')
      : null;
  return { baseUrl: '', token };
}

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null {
  return typeof localStorage !== 'undefined' ? localStorage : null;
}

/**
 * Adapt a wire-summary into the slice's domain shape. Today they're identical
 * but the indirection lets the wire shape evolve without rippling through
 * consumers.
 */
function fromWireSummary(s: ViewSummaryWire): ViewSummary {
  return {
    viewId: s.viewId,
    viewName: s.viewName,
    lastModified: s.lastModified,
    // IV3-S5 — raster rows show a PNG thumbnail; deterministic rows keep the
    // server-provided SVG thumbnail unchanged.
    thumbnailUrl:
      s.manifest?.engine === 'llm-image'
        ? imageThumbnailUrl(s.viewId)
        : s.thumbnailUrl,
    sourcePath: s.sourcePath,
    ...(s.manifest !== undefined ? { manifest: s.manifest } : {}),
  };
}

/**
 * LV2-S2 — return a manifest summary with `leadEngine` patched. When the view
 * has no manifest yet, mint a minimal placeholder carrying only the lead choice
 * (engine defaults to `'deterministic'`, source path unknown); the server is the
 * SSOT and the next `loadViewsList` reconciles the full manifest. The sketch +
 * SysML source remain reachable regardless of `leadEngine` — it only chooses the
 * default surface.
 */
function withLeadEngine(
  manifest: ViewManifestSummary | undefined,
  leadEngine: 'sketch' | 'llm-image',
): ViewManifestSummary {
  if (manifest === undefined) {
    return { engine: 'deterministic', sysmlSourcePath: '', leadEngine };
  }
  return { ...manifest, leadEngine };
}

function readPinnedFromStorage(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
  key: string,
): Record<string, number> {
  if (!storage) return {};
  const raw = storage.getItem(key);
  if (raw === null || raw.length === 0) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

function writePinnedToStorage(
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
  key: string,
  map: Record<string, number>,
): void {
  if (!storage) return;
  try {
    if (Object.keys(map).length === 0) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, JSON.stringify(map));
    }
  } catch {
    // Storage quota / SecurityError — degrade silently. The in-memory pin
    // map remains correct for the active session.
  }
}

// ---- Slice creator -------------------------------------------------------

export const createViewsSlice =
  (config: ViewsSliceConfig = {}): StateCreator<ViewsSlice> =>
  (set, get) => {
    const api = config.api ?? defaultViewsWireAdapter;
    const ctx = config.apiContext ?? defaultApiContext();
    const storage = config.storage ?? defaultStorage();
    const now = config.now ?? (() => Date.now());

    // Hydrate persisted pins synchronously so the first render sees them.
    const initialPins = readPinnedFromStorage(storage, VIEWS_INITIAL_STATE.pinnedPersistKey);

    return {
      ...VIEWS_INITIAL_STATE,
      pinnedByViewId: initialPins,

      loadViewsList: async () => {
        set((s) => ({ ...s, loading: true, lastError: null }));
        const res = await api.listViews(ctx);
        if (res.ok) {
          set((s) => ({
            ...s,
            viewsList: res.value.map(fromWireSummary),
            loading: false,
            lastError: null,
          }));
          return { ok: true, value: undefined };
        }
        set((s) => ({ ...s, loading: false, lastError: res.error }));
        return { ok: false, error: res.error };
      },

      pinView: (viewId, pinned) => {
        const next = { ...get().pinnedByViewId };
        if (pinned) {
          next[viewId] = now();
        } else {
          delete next[viewId];
        }
        writePinnedToStorage(storage, get().pinnedPersistKey, next);
        set((s) => ({ ...s, pinnedByViewId: next }));
      },

      renameView: async (viewId, newName) => {
        // Optimistic — update the in-memory list eagerly.
        const before = get().viewsList;
        set((s) => ({
          ...s,
          viewsList: s.viewsList.map((v) =>
            v.viewId === viewId ? { ...v, viewName: newName } : v,
          ),
        }));
        const res = await api.renameView(ctx, viewId, newName);
        if (!res.ok) {
          set((s) => ({ ...s, viewsList: before, lastError: res.error }));
          return { ok: false, error: res.error };
        }
        // Server may have sanitised the name; re-apply it.
        set((s) => ({
          ...s,
          viewsList: s.viewsList.map((v) =>
            v.viewId === viewId
              ? { ...v, viewId: res.value.viewId, viewName: res.value.viewName }
              : v,
          ),
          lastError: null,
        }));
        return {
          ok: true,
          value: { viewId: res.value.viewId, viewName: res.value.viewName },
        };
      },

      deleteView: async (viewId) => {
        const before = get().viewsList;
        const beforePins = get().pinnedByViewId;
        // Optimistic remove.
        set((s) => {
          const nextPins = { ...s.pinnedByViewId };
          delete nextPins[viewId];
          return {
            ...s,
            viewsList: s.viewsList.filter((v) => v.viewId !== viewId),
            pinnedByViewId: nextPins,
          };
        });
        const res = await api.deleteView(ctx, viewId);
        if (!res.ok) {
          set((s) => ({
            ...s,
            viewsList: before,
            pinnedByViewId: beforePins,
            lastError: res.error,
          }));
          return { ok: false, error: res.error };
        }
        writePinnedToStorage(storage, get().pinnedPersistKey, get().pinnedByViewId);
        return { ok: true, value: undefined };
      },

      deleteAllViews: async () => {
        const prev = get();
        // Optimistic clear of every view-scoped local slice field. If the
        // server call fails we roll the whole snapshot back so the UI never
        // drifts from disk.
        set((s) => ({
          ...s,
          viewsList: [],
          pinnedByViewId: {},
          activeViewId: null,
          layoutsById: {},
          dirtyByViewId: {},
          lastSavedAtByViewId: {},
          layoutIdByViewId: {},
        }));
        const res = await api.deleteAllViews(ctx);
        if (!res.ok) {
          set((s) => ({
            ...s,
            viewsList: prev.viewsList,
            pinnedByViewId: prev.pinnedByViewId,
            activeViewId: prev.activeViewId,
            layoutsById: prev.layoutsById,
            dirtyByViewId: prev.dirtyByViewId,
            lastSavedAtByViewId: prev.lastSavedAtByViewId,
            layoutIdByViewId: prev.layoutIdByViewId,
            lastError: res.error,
          }));
          return { ok: false, error: res.error };
        }
        writePinnedToStorage(storage, get().pinnedPersistKey, {});
        return { ok: true, value: { deleted: res.value.deleted } };
      },

      exportPng: async (viewId) => {
        return api.exportPng(ctx, viewId);
      },

      setActiveViewId: (viewId) => {
        set((s) => ({ ...s, activeViewId: viewId }));
      },

      refreshAfterListChanged: async () => {
        await get().loadViewsList();
      },

      // ── W4-S2 actions ─────────────────────────────────────────────────
      loadView: async (viewId) => {
        const getLayoutFn = api.getLayout;
        if (getLayoutFn === undefined) {
          // No-op fallback when no adapter; tests must wire `getLayout` if
          // they exercise this path.
          const error: ApiError = {
            code: 'CAPABILITY_MISSING',
            message: 'getLayout adapter not wired',
            retryable: false,
            requestId: 'r-local',
          };
          return { ok: false, error };
        }
        const res = await getLayoutFn(ctx, viewId);
        if (!res.ok) {
          set((s) => ({ ...s, lastError: res.error }));
          return { ok: false, error: res.error };
        }
        set((s) => ({
          ...s,
          layoutsById: { ...s.layoutsById, [viewId]: res.value },
          dirtyByViewId: { ...s.dirtyByViewId, [viewId]: false },
        }));
        return { ok: true, value: res.value };
      },

      saveLayout: async (viewId, layout) => {
        const saveLayoutFn = api.saveLayout;
        if (saveLayoutFn === undefined) {
          const error: ApiError = {
            code: 'CAPABILITY_MISSING',
            message: 'saveLayout adapter not wired',
            retryable: false,
            requestId: 'r-local',
          };
          return { ok: false, error };
        }
        // Optimistically cache the layout + clear dirty.
        const prev = get();
        const prevLayout = prev.layoutsById[viewId];
        const prevDirty = prev.dirtyByViewId[viewId] ?? false;
        set((s) => ({
          ...s,
          layoutsById: { ...s.layoutsById, [viewId]: layout },
          dirtyByViewId: { ...s.dirtyByViewId, [viewId]: false },
        }));
        const res = await saveLayoutFn(ctx, viewId, layout);
        if (!res.ok) {
          // Rollback the optimistic clear so the renderer's [Save] button
          // can retry without losing the dirty signal.
          set((s) => {
            const layouts = { ...s.layoutsById };
            if (prevLayout !== undefined) {
              layouts[viewId] = prevLayout;
            } else {
              delete layouts[viewId];
            }
            return {
              ...s,
              layoutsById: layouts,
              dirtyByViewId: { ...s.dirtyByViewId, [viewId]: prevDirty || true },
              lastError: res.error,
            };
          });
          return { ok: false, error: res.error };
        }
        set((s) => ({
          ...s,
          lastSavedAtByViewId: { ...s.lastSavedAtByViewId, [viewId]: now() },
          layoutIdByViewId: { ...s.layoutIdByViewId, [viewId]: res.value.layoutId },
          lastError: null,
        }));
        return { ok: true, value: res.value };
      },

      markViewDirty: (viewId) => {
        set((s) => ({
          ...s,
          dirtyByViewId: { ...s.dirtyByViewId, [viewId]: true },
        }));
      },

      exportSvg: async (viewId, layoutId) => {
        const exportSvgFn = api.exportSvg;
        if (exportSvgFn === undefined) {
          const error: ApiError = {
            code: 'CAPABILITY_MISSING',
            message: 'exportSvg adapter not wired',
            retryable: false,
            requestId: 'r-local',
          };
          return { ok: false, error };
        }
        const effectiveLayoutId = layoutId ?? get().layoutIdByViewId[viewId];
        return exportSvgFn(ctx, viewId, effectiveLayoutId);
      },

      setLeadEngine: async (viewId, leadEngine) => {
        const setLeadEngineFn = api.setLeadEngine;
        if (setLeadEngineFn === undefined) {
          const error: ApiError = {
            code: 'CAPABILITY_MISSING',
            message: 'setLeadEngine adapter not wired',
            retryable: false,
            requestId: 'r-local',
          };
          set((s) => ({ ...s, lastError: error }));
          return { ok: false, error };
        }
        // Optimistic in-memory patch of ONLY the matching view's manifest.
        // A view with no manifest yet gets a minimal one carrying just the
        // lead choice (the server is the SSOT and will reconcile on next list).
        const before = get().viewsList;
        set((s) => ({
          ...s,
          viewsList: s.viewsList.map((v) =>
            v.viewId === viewId ? { ...v, manifest: withLeadEngine(v.manifest, leadEngine) } : v,
          ),
          lastError: null,
        }));
        const res = await setLeadEngineFn(ctx, viewId, leadEngine);
        if (!res.ok) {
          // Roll the whole list back so only-the-target invariant holds even
          // under a failed write (no partial drift on other views).
          set((s) => ({ ...s, viewsList: before, lastError: res.error }));
          return { ok: false, error: res.error };
        }
        // Re-apply the server's confirmed value (defends against a server-side
        // coercion) on the target view only.
        set((s) => ({
          ...s,
          viewsList: s.viewsList.map((v) =>
            v.viewId === viewId
              ? { ...v, manifest: withLeadEngine(v.manifest, res.value.leadEngine) }
              : v,
          ),
          lastError: null,
        }));
        return { ok: true, value: { leadEngine: res.value.leadEngine } };
      },

      clearScope: async (viewId) => {
        const clearScopeFn = api.clearScope;
        if (clearScopeFn === undefined) {
          const error: ApiError = {
            code: 'CAPABILITY_MISSING',
            message: 'clearScope adapter not wired',
            retryable: false,
            requestId: 'r-local',
          };
          set((s) => ({ ...s, lastError: error }));
          return { ok: false, error };
        }
        // Optimistic: strip `viewScope` from the cached layout (if present) so a
        // slice-backed renderer re-derives the full node set. The card/renderer
        // that fetches via viewsApi.getLayout directly re-fetches on ok.
        const prevLayout = get().layoutsById[viewId];
        if (prevLayout !== undefined) {
          const { viewScope: _drop, ...rest } = prevLayout as ReactFlowLayoutJson & {
            viewScope?: unknown;
          };
          set((s) => ({ ...s, layoutsById: { ...s.layoutsById, [viewId]: rest }, lastError: null }));
        }
        const res = await clearScopeFn(ctx, viewId);
        if (!res.ok) {
          if (prevLayout !== undefined) {
            set((s) => ({ ...s, layoutsById: { ...s.layoutsById, [viewId]: prevLayout } }));
          }
          set((s) => ({ ...s, lastError: res.error }));
          return { ok: false, error: res.error };
        }
        return { ok: true, value: res.value };
      },

      _resetViews: () => {
        // Preserve action references — only state fields are reset.
        const initial = { ...VIEWS_INITIAL_STATE, pinnedByViewId: { ...initialPins } };
        set((s) => ({ ...s, ...initial }));
      },
    };
  };

/** Vanilla-store factory — used by tests for isolated state. */
export function createViewsStore(config: ViewsSliceConfig = {}) {
  return createStore<ViewsSlice>()(createViewsSlice(config));
}

// ---- IV3-S5 raster-coexistence selectors / helpers -----------------------

/**
 * PNG thumbnail / full-image URL for a raster view. The IV2 route family
 * serves the bytes at `GET /api/v1/views/<id>/image`.
 */
export function imageThumbnailUrl(viewId: string): string {
  return `/api/v1/views/${encodeURIComponent(viewId)}/image`;
}

/** True when the view's manifest engine is the LLM raster engine. */
export function isRasterView(view: ViewSummary): boolean {
  return view.manifest?.engine === 'llm-image';
}

/**
 * LV2-S2 — the EFFECTIVE sticky lead engine for a view. Absent manifest /
 * absent `leadEngine` ⇒ `'sketch'` (the byte-identical default; legacy +
 * deterministic views never stamp the field). This is the single read-site that
 * encodes the "absent ⇒ sketch" rule so the SPA leads with the right surface
 * while ALWAYS keeping the other representation + the SysML source one action
 * away (the lead chooses the default surface, never removes the alternative).
 */
export function effectiveLeadEngine(view: ViewSummary): 'sketch' | 'llm-image' {
  return view.manifest?.leadEngine ?? 'sketch';
}

/**
 * Staleness check — a raster view is STALE when its manifest's
 * `sceneContentHash` diverges from the current scene hash (when known). When
 * either hash is absent the view is treated as not-stale (forward-compatible
 * with manifest-less / hash-less views).
 */
export function isRasterViewStale(
  view: ViewSummary,
  currentSceneHash: string | undefined,
): boolean {
  const stamped = view.manifest?.sceneContentHash;
  if (stamped === undefined || currentSceneHash === undefined) return false;
  return stamped !== currentSceneHash;
}

// ---- LV1-S5 — persisted layout-intent re-apply selectors ------------------
//
// When a view's `layout.json` carries an applied `LayoutIntent` plus the
// already-RESOLVED `elkOptions` + `positions` (persisted via the layout PUT
// route after the user applied a layout request), the SPA must re-apply that
// arrangement on load WITHOUT re-calling the layout-director LLM (AC2). These
// pure selectors expose the persisted directives for the renderer to consume;
// they NEVER re-compile the intent — `elkOptions`/`positions` are stored
// pre-resolved (incl. any LV1-S3 nesting-unsafe-algorithm downgrade), so the
// re-apply is a verbatim replay, not a re-derivation.
//
// CLAUDE.md Result<T,E> note: these are total pure selectors over an in-memory
// object — they cannot fail and have no cross-boundary I/O, so (like
// `defaultLayoutIntent()` in base) they return plain values, not `Result`.

/**
 * The resolved re-apply directives lifted off a persisted layout. `null` when
 * the layout carries NO persisted intent — the caller then falls through to
 * today's exact no-intent path (golden parity for the switch-OFF case).
 */
export interface PersistedLayoutDirectives {
  /** The applied intent (provenance / LV1-S6 follow-ups) when present. */
  layoutIntent?: LayoutIntent;
  /** Resolved root ELK options to seed the layout pass (verbatim replay). */
  elkOptions?: Record<string, string>;
  /** Resolved node positions to overlay after layout (user/applied placement). */
  positions?: Record<string, { x: number; y: number }>;
}

/**
 * True when a layout carries ANY persisted re-apply directive (`layoutIntent`
 * or resolved `elkOptions`). A bare `positions`-only layout (the existing
 * manual-drag persistence) is NOT a layout-intent — it returns `false` so the
 * legacy drag-persist path stays byte-identical.
 */
export function hasPersistedLayoutIntent(layout: ReactFlowLayoutJson): boolean {
  return layout.layoutIntent !== undefined || layout.elkOptions !== undefined;
}

/**
 * Lift the persisted re-apply directives off a layout, or `null` when there is
 * no persisted intent (parity fall-through). Only fields actually present are
 * included, so a partial persist (e.g. intent without resolved opts) round-trips
 * exactly what was stored.
 */
export function resolvePersistedLayout(
  layout: ReactFlowLayoutJson,
): PersistedLayoutDirectives | null {
  if (!hasPersistedLayoutIntent(layout)) {
    return null;
  }
  const out: PersistedLayoutDirectives = {};
  if (layout.layoutIntent !== undefined) out.layoutIntent = layout.layoutIntent;
  if (layout.elkOptions !== undefined) out.elkOptions = layout.elkOptions;
  if (layout.positions !== undefined) out.positions = layout.positions;
  return out;
}
